import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const LogsFilterPanel = ({ 
  filters, 
  onFiltersChange, 
  onExport, 
  totalLogs, 
  filteredLogs,
  isExporting = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // ✅ ADD THIS: Get today's date for max date validation
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const maxDate = getTodayDate();

  // ... rest of the code (userOptions, doorOptions, etc.) ...

  const userOptions = [
    { value: '', label: 'All Users' },
    { value: 'admin', label: 'Admin User' },
    { value: 'john.doe', label: 'John Doe' },
    { value: 'jane.smith', label: 'Jane Smith' },
    { value: 'mike.wilson', label: 'Mike Wilson' },
    { value: 'sarah.johnson', label: 'Sarah Johnson' },
    { value: 'david.brown', label: 'David Brown' },
    { value: 'lisa.davis', label: 'Lisa Davis' },
    { value: 'tom.anderson', label: 'Tom Anderson' },
    { value: 'emma.taylor', label: 'Emma Taylor' },
    { value: 'alex.martinez', label: 'Alex Martinez' }
  ];

  const doorOptions = [
    { value: '', label: 'All Doors' },
    { value: 'SL-001', label: 'Main Entrance (SL-001)' },
    { value: 'SL-002', label: 'Server Room (SL-002)' },
    { value: 'SL-003', label: 'Executive Office (SL-003)' },
    { value: 'SL-004', label: 'Conference Room A (SL-004)' },
    { value: 'SL-005', label: 'Storage Room (SL-005)' },
    { value: 'SL-006', label: 'Emergency Exit (SL-006)' },
    { value: 'SL-007', label: 'Parking Garage (SL-007)' },
    { value: 'SL-008', label: 'Rooftop Access (SL-008)' }
  ];

  const actionOptions = [
    { value: '', label: 'All Actions' },
    { value: 'Granted', label: 'Access Granted' },
    { value: 'Denied', label: 'Access Denied' }
  ];

  const anomalyOptions = [
    { value: '', label: 'All Logs' },
    { value: 'true', label: 'Anomalies Only' },
    { value: 'false', label: 'Normal Activity' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      dateFrom: '',
      dateTo: '',
      userId: '',
      doorId: '',
      action: '',
      anomaly: '',
      search: ''
    });
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value !== '');

  return (
    <div className="bg-card border border-border rounded-lg security-shadow-card">
      {/* Mobile Filter Toggle */}
      <div className="md:hidden flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Filter" size={20} className="text-muted-foreground" />
          <div>
            <h3 className="text-sm font-medium text-foreground">Filters</h3>
            <p className="text-xs text-muted-foreground">
              {filteredLogs} of {totalLogs} logs
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
          className="text-muted-foreground"
        >
          {isExpanded ? 'Hide' : 'Show'}
        </Button>
      </div>

      {/* Filter Content */}
      <div className={`${isExpanded ? 'block' : 'hidden'} md:block p-4`}>
        {/* Header - Desktop Only */}
        <div className="hidden md:flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Icon name="Filter" size={20} className="text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground">Filter Access Logs</h3>
            <span className="bg-muted text-muted-foreground px-2 py-1 rounded-md text-sm">
              {filteredLogs} of {totalLogs}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                iconName="X"
                className="text-muted-foreground hover:text-foreground"
              >
                Clear All
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              loading={isExporting}
              iconName="Download"
              disabled={filteredLogs === 0}
            >
              Export CSV
            </Button>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date Range - ✅ WITH VALIDATION */}
          <div className="space-y-2">
            <Input
              label="From Date"
              type="date"
              value={filters?.dateFrom}
              max={maxDate}
              onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
              className="w-full"
            />
          </div>
          
          <div className="space-y-2">
            <Input
              label="To Date"
              type="date"
              value={filters?.dateTo}
              max={maxDate}
              min={filters?.dateFrom || undefined}
              onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
              className="w-full"
            />
          </div>

          {/* User Filter */}
          <div className="space-y-2">
            <Select
              label="User"
              options={userOptions}
              value={filters?.userId}
              onChange={(value) => handleFilterChange('userId', value)}
              searchable
              clearable
            />
          </div>

          {/* Door Filter */}
          <div className="space-y-2">
            <Select
              label="Door"
              options={doorOptions}
              value={filters?.doorId}
              onChange={(value) => handleFilterChange('doorId', value)}
              searchable
              clearable
            />
          </div>

          {/* Action Filter */}
          <div className="space-y-2">
            <Select
              label="Action"
              options={actionOptions}
              value={filters?.action}
              onChange={(value) => handleFilterChange('action', value)}
            />
          </div>

          {/* Anomaly Filter */}
          <div className="space-y-2">
            <Select
              label="Anomaly Status"
              options={anomalyOptions}
              value={filters?.anomaly}
              onChange={(value) => handleFilterChange('anomaly', value)}
            />
          </div>

          {/* Search */}
          <div className="md:col-span-2 space-y-2">
            <Input
              label="Search Logs"
              type="search"
              placeholder="Search by user, door, or details..."
              value={filters?.search}
              onChange={(e) => handleFilterChange('search', e?.target?.value)}
            />
          </div>
        </div>

        {/* Mobile Actions */}
        <div className="md:hidden flex items-center justify-between mt-4 pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Showing {filteredLogs} of {totalLogs} logs
          </div>
          <div className="flex items-center space-x-2">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                iconName="X"
              >
                Clear
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              loading={isExporting}
              iconName="Download"
              disabled={filteredLogs === 0}
            >
              Export
            </Button>
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex flex-wrap gap-2">
              {filters?.dateFrom && (
                <div className="flex items-center space-x-1 bg-accent/10 text-accent px-2 py-1 rounded-md text-sm">
                  <span>From: {filters?.dateFrom}</span>
                  <button
                    onClick={() => handleFilterChange('dateFrom', '')}
                    className="hover:text-accent-foreground"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
              )}
              {filters?.dateTo && (
                <div className="flex items-center space-x-1 bg-accent/10 text-accent px-2 py-1 rounded-md text-sm">
                  <span>To: {filters?.dateTo}</span>
                  <button
                    onClick={() => handleFilterChange('dateTo', '')}
                    className="hover:text-accent-foreground"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
              )}
              {filters?.userId && (
                <div className="flex items-center space-x-1 bg-accent/10 text-accent px-2 py-1 rounded-md text-sm">
                  <span>User: {userOptions?.find(u => u?.value === filters?.userId)?.label}</span>
                  <button
                    onClick={() => handleFilterChange('userId', '')}
                    className="hover:text-accent-foreground"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
              )}
              {filters?.doorId && (
                <div className="flex items-center space-x-1 bg-accent/10 text-accent px-2 py-1 rounded-md text-sm">
                  <span>Door: {doorOptions?.find(d => d?.value === filters?.doorId)?.label}</span>
                  <button
                    onClick={() => handleFilterChange('doorId', '')}
                    className="hover:text-accent-foreground"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
              )}
              {filters?.action && (
                <div className="flex items-center space-x-1 bg-accent/10 text-accent px-2 py-1 rounded-md text-sm">
                  <span>{actionOptions?.find(a => a?.value === filters?.action)?.label}</span>
                  <button
                    onClick={() => handleFilterChange('action', '')}
                    className="hover:text-accent-foreground"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
              )}
              {filters?.anomaly && (
                <div className="flex items-center space-x-1 bg-accent/10 text-accent px-2 py-1 rounded-md text-sm">
                  <span>{anomalyOptions?.find(a => a?.value === filters?.anomaly)?.label}</span>
                  <button
                    onClick={() => handleFilterChange('anomaly', '')}
                    className="hover:text-accent-foreground"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
              )}
              {filters?.search && (
                <div className="flex items-center space-x-1 bg-accent/10 text-accent px-2 py-1 rounded-md text-sm">
                  <span>Search: "{filters?.search}"</span>
                  <button
                    onClick={() => handleFilterChange('search', '')}
                    className="hover:text-accent-foreground"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogsFilterPanel;
